import { useContext, useEffect, useMemo, useState } from 'react'
import { UserContext } from '../context/UserContext'
import { LocationContext } from '../context/LocationContext'
import { getServiceAvailability } from '../api/app-setting/app-setting'
import { availabilityHelpers, SERVICE_AVAILABILITY } from './service-availability'

// Settings per "country|city", fetched once per page load. Admin changes reach
// the app on the next load, which is what the admin screen tells them.
const cache = new Map()

const toAvailability = (rows) => {
    const availability = {}
    for (const row of rows || []) {
        if (!row?.requestType) continue
        availability[row.requestType] = {
            showOnHome: row.showOnHome !== false,
            paused: Boolean(row.paused),
            allowAdvancePaymentWhenPaused: Boolean(row.allowAdvancePaymentWhenPaused),
            pausedSubTypes: Array.isArray(row.pausedSubTypes) ? row.pausedSubTypes : [],
            pausedMessage: row.pausedMessage || '',
        }
    }
    return availability
}

const load = (countryName, city) => {
    const key = `${countryName}|${city || ''}`
    if (!cache.has(key)) {
        cache.set(key, getServiceAvailability(countryName, city)
            .then(toAvailability)
            .catch((error) => {
                // Unreachable: keep the built-in values (utils/service-availability.js)
                // and try again on the next mount
                console.error('Service availability unavailable, using the built-in values', error)
                cache.delete(key)
                return SERVICE_AVAILABILITY
            }))
    }
    return cache.get(key)
}

/**
 * Which services can be booked in the customer's market: the helpers from
 * utils/service-availability.js, bound to the admin settings for the signed-in
 * user's country and city (or, signed out, the country the site resolved). Until
 * the settings arrive, and if they can't be fetched, the built-in values apply.
 */
export const useServiceAvailability = () => {
    const { userCountryName, userCity } = useContext(UserContext) || {}
    const { countryName: locationCountryName } = useContext(LocationContext) || {}
    const countryName = userCountryName || locationCountryName || 'Singapore'
    // A profile city from another country (moved, old account) would never match
    const city = userCountryName ? userCity : undefined
    const [loaded, setLoaded] = useState({ key: null, availability: null })
    const key = `${countryName}|${city || ''}`

    useEffect(() => {
        let cancelled = false
        load(countryName, city).then((availability) => {
            if (!cancelled) setLoaded({ key: `${countryName}|${city || ''}`, availability })
        })
        return () => { cancelled = true }
    }, [countryName, city])

    const availability = loaded.key === key ? loaded.availability : null
    return useMemo(() => availabilityHelpers(availability || SERVICE_AVAILABILITY), [availability])
}
